import React, {useState, useEffect, useCallback} from 'react'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {useSubscription} from '@/shared/utils'
import {isSubscriptionExpired} from '@/shared/utils/subscriptionLifecycle'
import {CustomModal} from '@/app/modules/widgets/components/CustomModal'
import {PurchaseActionButton} from '@/app/modules/purchases/PurchaseActionButton'
import {QuoteRequestDialog} from '@/app/modules/purchases/QuoteRequestDialog'
import {
  listMembers,
  inviteMember,
  removeMember,
  updateMemberRole,
  updateMemberUnitsLimit,
  resetMemberUnits,
  Member,
  ListMembersResponse,
} from './api'

interface ManageUsersSectionProps {
  subscriptionId: string
}

export const ManageUsersSection: React.FC<ManageUsersSectionProps> = ({subscriptionId}) => {
  const {
    role,
    subscriptionDetails,
    subscriptionLifecycle,
    maxSeats,
    usedSeats: usedSeatsFromContext,
  } = useSubscription()
  const [membersData, setMembersData] = useState<ListMembersResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [isInviting, setIsInviting] = useState(false)
  const [removingMember, setRemovingMember] = useState<string | null>(null)
  const [updatingRole, setUpdatingRole] = useState<string | null>(null)
  const [editingUnits, setEditingUnits] = useState<string | null>(null)
  const [unitsLimitInput, setUnitsLimitInput] = useState('')
  const [savingUnits, setSavingUnits] = useState<string | null>(null)
  const [resettingUnits, setResettingUnits] = useState<string | null>(null)
  const [showAddSeats, setShowAddSeats] = useState(false)
  const [confirmModal, setConfirmModal] = useState<{
    title: string
    message: string
    onConfirm: () => void
    confirmText?: string
    confirmClass?: string
  } | null>(null)

  const isManager = role === 'owner' || role === 'admin'

  const fetchMembers = useCallback(async () => {
    if (!isManager) return
    setIsLoading(true)
    try {
      const data = await listMembers(subscriptionId)
      setMembersData(data)
    } catch (err) {
      getLogger().error('Failed to load members:', err)
    } finally {
      setIsLoading(false)
    }
  }, [subscriptionId, isManager])

  // Patch a single member row in place without refetching the whole list.
  // Pass a partial to merge, or a function that returns the updated member.
  const patchMemberLocal = useCallback(
    (sub: string, patch: Partial<Member> | ((m: Member) => Member)) => {
      setMembersData((prev) =>
        prev
          ? {
              ...prev,
              members: prev.members.map((m) =>
                m.sub === sub ? (typeof patch === 'function' ? patch(m) : {...m, ...patch}) : m
              ),
            }
          : prev
      )
    },
    []
  )

  // Drop previous subscription's member list as soon as the id changes so we
  // do not show the wrong users next to the new key's details.
  useEffect(() => {
    setMembersData(null)
    setInviteEmail('')
    setEditingUnits(null)
    setUnitsLimitInput('')
  }, [subscriptionId])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    const email = inviteEmail.trim().toLowerCase()
    if (!email) return

    setIsInviting(true)
    try {
      await inviteMember(subscriptionId, email)
      toast.success(`Invitation sent to ${email}`, toastDefaultConfig)
      setInviteEmail('')
      // Invitations create INVITATION# rows, not USER# rows — the member list
      // only changes once the invitee accepts. No local update needed.
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to send invitation'
      toast.error(msg, toastDefaultConfig)
      getLogger().error('Invite failed:', err)
    } finally {
      setIsInviting(false)
    }
  }

  const handleRemove = (member: Member) => {
    setConfirmModal({
      title: 'Remove Member',
      message: `Remove ${member.email || member.sub} from this subscription?`,
      confirmText: 'Remove',
      confirmClass: 'btn btn-danger',
      onConfirm: async () => {
        setRemovingMember(member.sub)
        try {
          await removeMember(subscriptionId, member.sub)
          toast.success('Member removed', toastDefaultConfig)
          // Mark revoked locally — the activeMembers filter will hide the row —
          // and free up a seat in the usage bar.
          setMembersData((prev) =>
            prev
              ? {
                  ...prev,
                  members: prev.members.map((m) =>
                    m.sub === member.sub ? {...m, status: 'revoked'} : m
                  ),
                  used_seats: Math.max(0, prev.used_seats - 1),
                }
              : prev
          )
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Failed to remove member'
          toast.error(msg, toastDefaultConfig)
          getLogger().error('Remove member failed:', err)
        } finally {
          setRemovingMember(null)
        }
      },
    })
  }

  const handleToggleRole = (member: Member) => {
    const newRole = member.role === 'admin' ? 'member' : 'admin'
    const label = member.email || member.sub
    setConfirmModal({
      title: 'Change Role',
      message: `Change ${label} role to "${newRole}"?`,
      confirmText: 'Confirm',
      onConfirm: async () => {
        setUpdatingRole(member.sub)
        try {
          const res = await updateMemberRole(subscriptionId, member.sub, newRole)
          toast.success(`${label} is now ${newRole === 'admin' ? 'an admin' : 'a member'}`, toastDefaultConfig)
          patchMemberLocal(member.sub, {role: res.role})
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Failed to update role'
          toast.error(msg, toastDefaultConfig)
          getLogger().error('Update role failed:', err)
        } finally {
          setUpdatingRole(null)
        }
      },
    })
  }

  const handleStartEditUnits = (member: Member) => {
    setEditingUnits(member.sub)
    setUnitsLimitInput(member.units_limit != null ? String(member.units_limit) : '')
  }

  const handleCancelEditUnits = () => {
    setEditingUnits(null)
    setUnitsLimitInput('')
  }

  const handleSaveUnitsLimit = async (member: Member) => {
    const trimmed = unitsLimitInput.trim()
    const newLimit = trimmed === '' ? null : parseInt(trimmed, 10)

    if (newLimit !== null && (isNaN(newLimit) || newLimit < 0)) {
      toast.error('Units limit must be a non-negative number or empty for unlimited', toastDefaultConfig)
      return
    }

    setSavingUnits(member.sub)
    try {
      const res = await updateMemberUnitsLimit(subscriptionId, member.sub, newLimit)
      const label = newLimit != null ? `${newLimit.toLocaleString()} units` : 'unlimited'
      toast.success(`Units limit set to ${label} for ${member.email || member.sub}`, toastDefaultConfig)
      setEditingUnits(null)
      setUnitsLimitInput('')
      // Backend resets consumed units when a new limit is set (see warning text above).
      patchMemberLocal(member.sub, {
        units_limit: res.units_limit ?? undefined,
        units_consumed: 0,
      })
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to update units limit'
      toast.error(msg, toastDefaultConfig)
      getLogger().error('Update units limit failed:', err)
    } finally {
      setSavingUnits(null)
    }
  }

  const handleResetUnits = (member: Member) => {
    const label = member.email || member.sub
    setConfirmModal({
      title: 'Reset Cloud Units',
      message: `Reset consumed units for ${label} back to 0?`,
      confirmText: 'Reset',
      confirmClass: 'btn btn-warning',
      onConfirm: async () => {
        setResettingUnits(member.sub)
        try {
          const res = await resetMemberUnits(subscriptionId, member.sub)
          toast.success(`Consumed units reset for ${label}`, toastDefaultConfig)
          patchMemberLocal(member.sub, {units_consumed: res.units_consumed ?? 0})
        } catch (err: unknown) {
          const msg =
            err instanceof Error ? err.message : 'Failed to reset units'
          toast.error(msg, toastDefaultConfig)
          getLogger().error('Reset units failed:', err)
        } finally {
          setResettingUnits(null)
        }
      },
    })
  }

  if (!isManager) {
    return (
      <div className='ob-page__section d-flex flex-column flex-grow-1'>
        <h3 className='ob-page__section-title'>
          <i className='fas fa-users me-2' />
          Manage Users
        </h3>
        <div className='card flex-grow-1'>
          <div className='card-body d-flex flex-column align-items-center justify-content-center py-5'>
            <i className='fas fa-lock text-muted mb-3' style={{fontSize: '2rem'}} />
            <p className='text-muted text-center mb-0'>
              Only the subscription owner or an admin can manage members.
              <br />
              Please contact your subscription owner or admin to make changes.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const rolePriority: Record<string, number> = {owner: 0, admin: 1, member: 2}
  const activeMembers = (membersData?.members?.filter((m) => m.status !== 'revoked') || [])
    .sort((a, b) => {
      const pa = rolePriority[a.role] ?? 3
      const pb = rolePriority[b.role] ?? 3
      if (pa !== pb) return pa - pb
      return (a.name || a.email).localeCompare(b.name || b.email)
    })
  // Prefer listMembers for used count when loaded; before that, use provider
  // (updated optimistically on key switch) so the bar does not read 0 / old key.
  const maxSeatsDisplay = subscriptionDetails?.maxHosts ?? membersData?.max_seats ?? maxSeats ?? 0
  const usedSeatsDisplay =
    membersData != null ? membersData.used_seats : usedSeatsFromContext
  const seatsAvailable = maxSeatsDisplay - usedSeatsDisplay

  return (
    <div className='ob-page__section d-flex flex-column flex-grow-1'>
      {showAddSeats && (
        <QuoteRequestDialog
          kind='add_seat'
          subscriptionId={subscriptionId}
          ownedModules={subscriptionDetails?.modules}
          closeModal={() => setShowAddSeats(false)}
        />
      )}
      <h3 className='ob-page__section-title'>
        <i className='fas fa-users me-2' />
        Manage Users
      </h3>
      <p className='ob-text--muted ob-text--small ob-spacing--mb-6'>
        Invite team members to share your subscription. Each member gets their own account and storage.
      </p>

      {/* Seat usage bar */}
      <div className='card mb-4'>
        <div className='card-body py-3'>
          <div className='d-flex justify-content-between align-items-center gap-2 mb-2'>
            <span className='fw-bold'>Seat Usage</span>
            <span className='text-muted ms-auto'>
              {usedSeatsDisplay} / {maxSeatsDisplay} seats used
            </span>
            {/* An expired subscription only offers renewal (Subscription Details card). */}
            {!isSubscriptionExpired(subscriptionLifecycle) && (
              <PurchaseActionButton
                label='Add Seats'
                title='Add seats to this subscription'
                onClick={() => setShowAddSeats(true)}
              />
            )}
          </div>
          {maxSeatsDisplay > 0 && (
            <div className='progress' style={{height: '8px'}}>
              <div
                className={`progress-bar ${
                  seatsAvailable < 0 ? 'bg-danger' : seatsAvailable === 0 ? 'bg-secondary' : 'bg-primary'
                }`}
                role='progressbar'
                style={{
                  width: `${Math.min(
                    100,
                    (usedSeatsDisplay / maxSeatsDisplay) * 100
                  )}%`,
                }}
                aria-valuenow={usedSeatsDisplay}
                aria-valuemin={0}
                aria-valuemax={maxSeatsDisplay}
              />
            </div>
          )}
        </div>
      </div>

      {/* Invite form */}
      <div className='card mb-4'>
        <div className='card-body'>
          <h5 className='mb-3'>Invite a Member</h5>
          <form onSubmit={handleInvite} className='d-flex gap-2 flex-wrap'>
            <input
              type='email'
              className='form-control'
              style={{flex: '1 1 12rem', minWidth: '10rem'}}
              placeholder='Enter email address'
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={isInviting || seatsAvailable <= 0}
            />
            <button
              type='submit'
              className='btn btn-bioabm'
              disabled={isInviting || !inviteEmail.trim() || seatsAvailable <= 0}
              style={{whiteSpace: 'nowrap'}}
            >
              {isInviting ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
                  Sending...
                </>
              ) : (
                <>
                  <i className='fas fa-paper-plane me-2' />
                  Send Invite
                </>
              )}
            </button>
          </form>
          {seatsAvailable <= 0 && membersData && (
            <div className='form-text text-danger mt-2'>
              <i className='fas fa-exclamation-triangle me-1' />
              All seats are in use. Remove a member or upgrade your subscription to invite more.
            </div>
          )}
        </div>
      </div>

      {/* Members table */}
      <div className='card'>
        <div className='card-body'>
          <h5 className='mb-3'>Current Members</h5>
          {isLoading ? (
            <div className='text-center py-4'>
              <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
              Loading members...
            </div>
          ) : activeMembers.length === 0 ? (
            <p className='text-muted text-center py-3'>No members yet. Send an invitation to get started.</p>
          ) : (
            <div className='table-responsive ob-settings-table'>
              <table className='table table-hover align-middle mb-0' style={{fontSize: '0.9rem'}}>
                <thead style={{backgroundColor: 'var(--ob-gray-50)'}}>
                  <tr>
                    <th style={{padding: '0.5rem'}}>User</th>
                    <th style={{padding: '0.5rem'}}>Cloud Units</th>
                    <th style={{padding: '0.5rem'}}>Role</th>
                    <th style={{padding: '0.5rem', textAlign: 'center', width: '90px'}}>Make Admin</th>
                    <th style={{padding: '0.5rem', textAlign: 'center', width: '80px'}}>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {activeMembers.map((member) => {
                    const consumed = member.units_consumed ?? 0
                    const hasLimit = member.units_limit != null
                    const remaining = hasLimit
                      ? Math.max(0, member.units_limit! - consumed)
                      : null
                    const isEditing = editingUnits === member.sub

                    return (
                      <tr key={member.sub}>
                        {/* User */}
                        <td style={{padding: '0.5rem'}}>
                          <div>
                            {member.name && (
                              <div className='fw-semibold'>{member.name}</div>
                            )}
                            <div className={member.name ? 'text-muted small' : ''} style={{fontFamily: 'var(--ob-font-mono)', fontSize: '0.85em'}}>
                              {member.email || member.sub}
                            </div>
                          </div>
                        </td>

                        {/* Cloud Units */}
                        <td style={{padding: '0.5rem'}}>
                          {member.role === 'owner' ? null : isEditing ? (
                            <div>
                              <div className='d-flex align-items-center gap-2'>
                                <input
                                  type='number'
                                  className='form-control form-control-sm'
                                  style={{width: '100px'}}
                                  placeholder='Unlimited'
                                  value={unitsLimitInput}
                                  onChange={(e) => setUnitsLimitInput(e.target.value)}
                                  min={0}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveUnitsLimit(member)
                                    if (e.key === 'Escape') handleCancelEditUnits()
                                  }}
                                />
                                <button
                                  className='btn btn-sm btn-success'
                                  onClick={() => handleSaveUnitsLimit(member)}
                                  disabled={savingUnits === member.sub}
                                  title='Save'
                                >
                                  {savingUnits === member.sub ? (
                                    <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                                  ) : (
                                    <i className='fas fa-check' />
                                  )}
                                </button>
                                <button
                                  className='btn btn-sm btn-outline-secondary'
                                  onClick={handleCancelEditUnits}
                                  title='Cancel'
                                >
                                  <i className='fas fa-times' />
                                </button>
                              </div>
                              {consumed > 0 && (
                                <div className='text-warning small mt-1'>
                                  <i className='fas fa-exclamation-triangle me-1' />
                                  This will reset the current consumed units ({consumed.toLocaleString()})
                                </div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <div className='d-flex align-items-center gap-2'>
                                <span className='small'>
                                  <strong>Used:</strong> {consumed.toLocaleString()}
                                </span>
                                {consumed > 0 && (
                                  <button
                                    className='btn btn-sm btn-link text-warning p-0'
                                    onClick={() => handleResetUnits(member)}
                                    disabled={resettingUnits === member.sub}
                                    title='Reset consumed units to 0'
                                  >
                                    {resettingUnits === member.sub ? (
                                      <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                                    ) : (
                                      <i className='fas fa-undo-alt' style={{fontSize: '0.75rem'}} />
                                    )}
                                  </button>
                                )}
                              </div>
                              <div className='d-flex align-items-center gap-2'>
                                <span className='small'>
                                  <strong>Limit:</strong>{' '}
                                  {hasLimit ? member.units_limit!.toLocaleString() : <span className='text-muted'>Unlimited</span>}
                                </span>
                                <button
                                  className='btn btn-sm btn-link p-0'
                                  onClick={() => handleStartEditUnits(member)}
                                  title='Set units limit'
                                >
                                  <i className='fas fa-pencil-alt' style={{fontSize: '0.75rem'}} />
                                </button>
                              </div>
                              {remaining != null && (
                                <span className={`small ${remaining === 0 ? 'text-danger fw-bold' : 'text-muted'}`}>
                                  <strong>Left:</strong> {remaining.toLocaleString()}
                                </span>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Role */}
                        <td style={{padding: '0.5rem'}}>
                          {member.role === 'owner' && (
                            <span className='badge' style={{backgroundColor: '#888e00'}}>Owner</span>
                          )}
                          {member.role === 'admin' && (
                            <span className='badge text-dark' style={{backgroundColor: '#ffc107'}}>Admin</span>
                          )}
                          {member.role === 'member' && (
                            <span className='badge' style={{backgroundColor: '#1b2c67'}}>Member</span>
                          )}
                        </td>

                        {/* Make Admin */}
                        <td style={{padding: '0.5rem', textAlign: 'center'}}>
                          {member.role !== 'owner' && (
                            <div className='d-flex align-items-center justify-content-center gap-2'>
                              <div className='form-check form-switch mb-0' style={{minHeight: 'auto'}}>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  role='switch'
                                  checked={member.role === 'admin'}
                                  onChange={() => handleToggleRole(member)}
                                  disabled={updatingRole === member.sub}
                                  title={member.role === 'admin' ? 'Demote to member' : 'Promote to admin'}
                                    style={{
                                      width: '2em',
                                      height: '1em',
                                      backgroundColor: member.role === 'admin' ? '#6c757d' : '',
                                      borderColor: '#6c757d',
                                    }}
                                />
                              </div>
                              {updatingRole === member.sub && (
                                <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                              )}
                            </div>
                          )}
                        </td>

                        {/* Remove */}
                        <td style={{padding: '0.5rem', textAlign: 'center'}}>
                          {member.role !== 'owner' && (
                            <button
                              className='btn btn-sm btn-outline-danger'
                              onClick={() => handleRemove(member)}
                              disabled={removingMember === member.sub}
                              title='Remove member'
                            >
                              {removingMember === member.sub ? (
                                <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                              ) : (
                                <i className='fas fa-user-minus' />
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {confirmModal && (
        <CustomModal
          title={confirmModal.title}
          closeModal={() => setConfirmModal(null)}
          buttons={[
            {
              text: 'Cancel',
              className: 'btn btn-light',
              onClick: () => setConfirmModal(null),
            },
            {
              text: confirmModal.confirmText || 'Confirm',
              className: confirmModal.confirmClass || 'btn btn-primary',
              onClick: () => {
                confirmModal.onConfirm()
                setConfirmModal(null)
              },
            },
          ]}
        >
          <p className='mb-0'>{confirmModal.message}</p>
        </CustomModal>
      )}
    </div>
  )
}
